/**
 * Black Marble — INTRO CINEMATIC audio. Lazily fetches + decodes the 8 manifest
 * files (a non-looping score on the music bus, a looping rumble bed + one shot
 * per beat across the sfx / ambience buses) and schedules them onto the existing
 * AudioEngine sub-buses. Everything degrades to SILENCE with no console error if
 * a buffer is missing or the AudioContext / buses are absent — mirroring how
 * musicManager.ts and sfxSamples.ts treat absent assets. Browser autoplay policy
 * means real playback only begins after the first pointerdown / keydown gesture.
 */
import type { AudioEngine } from './audioEngine.js';
import type { MusicManager } from './musicManager.js';
import {
  INTRO_AUDIO_CUES,
  type IntroAudioBus,
  type IntroAudioCue,
} from '../hud/introTimeline.js';

const AUDIO_BASE = '/assets/audio';
const CROSSFADE_SEC = 1.5;

const cueUrl = (file: string): string => `${AUDIO_BASE}/${file}.mp3`;

const decodeCue = async (ctx: AudioContext, url: string): Promise<AudioBuffer | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await ctx.decodeAudioData(await response.arrayBuffer());
  } catch {
    return null;
  }
};

const rampGain = (ctx: AudioContext, param: AudioParam, target: number, sec: number): void => {
  const now = ctx.currentTime;
  param.cancelScheduledValues(now);
  param.setValueAtTime(param.value, now);
  param.linearRampToValueAtTime(target, now + sec);
};

/** A scheduled cue's live graph nodes, kept so dismiss can ramp+stop them cleanly. */
interface LiveSource {
  id: string;
  source: AudioBufferSourceNode;
  gain: GainNode;
  stopped: boolean;
}

/** Early-skip one-shot fade before stop (ms → s) so a skip mid-riser doesn't blast the menu. */
const ONE_SHOT_FADE_SEC = 0.25;

export class IntroAudio {
  private readonly buffers = new Map<string, AudioBuffer | null>();
  private loadPromise: Promise<void> | null = null;
  private armed = false;
  /** Cue ids already started by sync() — each cue fires exactly once per film. */
  private readonly started = new Set<string>();
  private scoreGain: GainNode | null = null;
  private droneGain: GainNode | null = null;
  /** Every live BufferSource so dismiss can ramp + stop them (no orphaned drone / riser bleed). */
  private readonly live: LiveSource[] = [];
  private readonly onGesture = (): void => this.handleGesture();

  constructor(private readonly engine: AudioEngine) {}

  /** Begin loading buffers and wait for the unlock gesture (or fire immediately
   * when the context is already running, e.g. on return-to-title). Idempotent. */
  arm(): void {
    if (this.armed) return;
    this.armed = true;
    this.engine.resume();
    void this.ensureLoaded();
    if (this.engine.audioContext?.state === 'running') {
      this.handleGesture();
      return;
    }
    window.addEventListener('pointerdown', this.onGesture, { once: true });
    window.addEventListener('keydown', this.onGesture, { once: true });
  }

  /**
   * Tear the intro mix down and hand the menu its looping bed back to MusicManager.
   * Fixes two cleanup bugs:
   *  1. The looping `drone` was never stopped → it bled under `music-menu.mp3`. We now
   *     ramp `drone 0.60→0` alongside `score 0.85→0` over 1.5s, then `stop()` the drone.
   *  2. Early-skip: any still-playing one-shot (e.g. a skip during the `emergence` riser)
   *     is ramped to 0 over ~250 ms and stopped BEFORE the score/drone crossfade, so it
   *     does not blast under the menu.
   * Uses linear ramps (these are near-silent floors, not exponential decays).
   */
  crossfadeToMenu(music: MusicManager): void {
    const ctx = this.engine.audioContext;
    if (ctx) {
      this.stopOneShots(ctx); // bug #2 — kill live risers before the crossfade
      if (this.scoreGain) rampGain(ctx, this.scoreGain.gain, 0, CROSSFADE_SEC);
      if (this.droneGain) {
        rampGain(ctx, this.droneGain.gain, 0, CROSSFADE_SEC); // bug #1 — fade the looping drone
        this.safeStop(this.findLive('drone'), ctx.currentTime + CROSSFADE_SEC);
      }
    }
    music.resume();
    music.setState({ context: 'menu', ducked: false });
  }

  /** Ramp every live one-shot (everything but score/drone) to 0 over 250 ms, then stop. */
  private stopOneShots(ctx: AudioContext): void {
    const at = ctx.currentTime + ONE_SHOT_FADE_SEC;
    for (const ls of this.live) {
      if (ls.id === 'score' || ls.id === 'drone') continue;
      rampGain(ctx, ls.gain.gain, 0, ONE_SHOT_FADE_SEC);
      this.safeStop(ls, at);
    }
  }

  private findLive(id: string): LiveSource | undefined {
    return this.live.find((ls) => ls.id === id);
  }

  /** Stop a source at most once (a naturally-ended source was never explicitly stopped). */
  private safeStop(ls: LiveSource | undefined, when: number): void {
    if (!ls || ls.stopped) return;
    ls.stopped = true;
    try {
      ls.source.stop(when);
    } catch {
      /* already stopped / ended — safe to ignore */
    }
  }

  dispose(): void {
    window.removeEventListener('pointerdown', this.onGesture);
    window.removeEventListener('keydown', this.onGesture);
  }

  private handleGesture(): void {
    this.engine.resume();
    // Decode only — starts are driven by sync(t) off the CineClock, so a slow
    // fetch/decode can never slip the cues against the camera anchors.
    void this.ensureLoaded();
  }

  private ensureLoaded(): Promise<void> {
    if (!this.loadPromise) this.loadPromise = this.loadAll();
    return this.loadPromise;
  }

  private async loadAll(): Promise<void> {
    const ctx = this.engine.audioContext;
    if (!ctx) return;
    await Promise.all(
      INTRO_AUDIO_CUES.map(async (cue) => {
        this.buffers.set(cue.id, await decodeCue(ctx, cueUrl(cue.file)));
      }),
    );
  }

  /**
   * CineClock-locked playback (Wave 1 parity): called every cinematic frame with
   * the clock's `t`. A cue starts the frame its window opens; if its buffer
   * decoded LATE the source starts offset INTO the buffer at the exact film
   * position, so camera anchors (rake 12.8–16.8, title toll 23.4) can never
   * drift. While the clock is paused (hidden tab), unstarted cues hold.
   */
  sync(t: number): void {
    const ctx = this.engine.audioContext;
    if (!ctx) return;
    for (const cue of INTRO_AUDIO_CUES) {
      if (this.started.has(cue.id) || t < cue.start) continue;
      const buffer = this.buffers.get(cue.id);
      if (!buffer) continue; // still decoding — retried next frame at the right offset
      this.started.add(cue.id);
      const offset = Math.max(0, t - cue.start);
      if (!cue.loop && offset >= buffer.duration) continue; // window already passed
      this.startCue(ctx, cue, buffer, cue.loop ? offset % buffer.duration : offset);
    }
  }

  private startCue(ctx: AudioContext, cue: IntroAudioCue, buffer: AudioBuffer, offset: number): void {
    const bus = this.busFor(cue.bus);
    if (!bus) return;
    const gain = ctx.createGain();
    gain.gain.value = cue.gain;
    gain.connect(bus);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = cue.loop;
    source.connect(gain);
    source.start(ctx.currentTime, offset);
    this.live.push({ id: cue.id, source, gain, stopped: false });
    if (cue.id === 'score') this.scoreGain = gain;
    if (cue.id === 'drone') this.droneGain = gain; // captured so crossfadeToMenu can stop the loop
  }

  private busFor(bus: IntroAudioBus): GainNode | null {
    if (bus === 'music') return this.engine.musicBus;
    if (bus === 'sfx') return this.engine.sfxBus;
    return this.engine.ambienceBus;
  }
}
